import React, { ChangeEvent } from 'react'
import { Form, TextAreaProps } from 'semantic-ui-react'
import CustomLabel from '../../components/common/custom-label/CustomLabel'
import { FreeTextQuestionProps } from '../../api/Data'

const FreeTextQuestion: React.FC<FreeTextQuestionProps> = ({
  question,
  value,
  onChange,
  clipIndex,
  errorMessage,
  addValidationError,
  removeValidationError,
}) => {
  const questionKey = `question_${question.id}_${clipIndex}`
  const validationErrorPrefix = `validation-error_${questionKey}_`

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>, data: TextAreaProps) => {
    removeValidationError(question, clipIndex)
    onChange(data.name as string, data.value, clipIndex)
  }

  const validateValue = (val: string): boolean => {
    if (!question.options?.length && !question.datasetKeys?.length) return true

    const validOptions = [
      ...(question.options || []).map(o => ({ value: o })),
      ...(question.datasetKeys || [])
    ] as { value: string, rowNumber: number | undefined }[]

    const match = validOptions.find(v => v.value === val)
    if (match) {
      if (question.datasetId && match.rowNumber) onChange(`${questionKey}_dataset-row-numbers`, [match.rowNumber], clipIndex)

      return true
    }

    if (question.datasetId) onChange(`${questionKey}_dataset-row-numbers`, [], clipIndex)

    const caseInsensitiveMatch = validOptions.find((option) => option.value.toLowerCase() === val.toLowerCase())
    if (caseInsensitiveMatch) {
      addValidationError(
        `The entered value is not valid (did you mean "${caseInsensitiveMatch}"?) - if you think this is incorrect, please contact the project organizer`,
        question,
        clipIndex
      )
      return false
    }

    addValidationError(
      'The entered value is not valid - if you think this is incorrect, please contact the project organizer',
      question,
      clipIndex
    )
    return false
  }

  const handleBlur = () => {
    if (!value) return

    if (question.options?.length || question.datasetKeys?.length) {
      validateValue(value)
    } else if (question.alias === 'email') {
      const emailRegex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/
      if (emailRegex.test(value)) removeValidationError(question, clipIndex)
      else addValidationError('Please enter a valid email address', question, clipIndex)
    }
  }

  return (
    <Form.Field required>
      <CustomLabel>
        <CustomLabel.Text text={question.text} />
      </CustomLabel>
      <Form.TextArea
        name={questionKey}
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        rows={3}
        style={{ minHeight: '80px' }}
        placeholder="Type here..."
        error={
          (errorMessage &&
            errorMessage.includes(`${clipIndex}-${questionKey}`) && {
              content: 'This field is required',
              pointing: 'above',
            }) ||
          (errorMessage &&
            errorMessage.some((m) => m.startsWith(validationErrorPrefix)) && {
              content: errorMessage
                .find((m) => m.startsWith(validationErrorPrefix))
                ?.replace(validationErrorPrefix, ''),
              pointing: 'above',
            })
        }
      />
    </Form.Field>
  )
}

export default FreeTextQuestion
